import { useEffect, useRef, useState } from 'react';
import type { RealtimeEvent, RealtimeEventType } from '@captain-tasko/shared';

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface UseRealtimeOptions {
  types?: RealtimeEventType[];
  onEvent?: (event: RealtimeEvent) => void;
}

export function useRealtime({ types, onEvent }: UseRealtimeOptions): ConnectionState {
  const [state, setState] = useState<ConnectionState>('connecting');
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    const qs = types && types.length ? `?types=${types.join(',')}` : '';
    const url = `/api/v1/events${qs}`;
    const es = new EventSource(url, { withCredentials: true });

    es.onopen = () => setState('open');
    es.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data) as RealtimeEvent;
        handlerRef.current?.(data);
      } catch {
        // ignore malformed
      }
    };
    es.onerror = () => setState((s) => (s === 'open' ? 'reconnecting' : s));

    // Attach typed listeners so consumers can dispatch by event type.
    if (types) {
      for (const t of types) {
        es.addEventListener(t, (msg) => {
          try {
            const data = JSON.parse((msg as MessageEvent).data) as RealtimeEvent;
            handlerRef.current?.(data);
          } catch {
            // ignore
          }
        });
      }
    }

    return () => {
      setState('closed');
      es.close();
    };
  }, [types?.join(',')]);

  return state;
}
